import React, { SVGProps } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { SVGPathData } from 'svg-pathdata';
import * as MuiUtils from '@material-ui/core/utils';

interface BoxData {
  x: number
  y: number
  width: number
  height: number
}

// default icon size in pixels
const ICON_SIZE = 24;

// finds svg recursively
const extractSvg = (children: HTMLCollection): Element | null => {
  for (const child of Array.from(children)) {
    if (/^svg$/i.test(child.tagName)) return child;

    if (!!child.children.length) {
      const _svg = extractSvg(child.children);
      if (_svg) return _svg;
    }
  }
  return null;
};

// parses box data from svg
const parseBoxData = (svg: Element): BoxData => {
  const result = {x: 0, y: 0, width: 0, height: 0};
  const viewBox = svg.getAttribute('viewBox');

  result.width = Number(svg.getAttribute('width') || 0);
  result.height =  Number(svg.getAttribute('height') || 0);

  if (viewBox) {
    const [_x, _y, _width, _height] = viewBox.split(' ');
    result.x = Number(_x);
    result.y = Number(_y);
    result.width = Number(_width);
    result.height = Number(_height);
  }

  return result;
};

// fixes path data
const fixPathData = (pathData: SVGPathData, boxData: BoxData) => {
  const { x, y, width, height } = boxData;
  const scale = ICON_SIZE / Math.max(width, height);

  // fix svg icon position
  if (x !== 0 || y !== 0) pathData.translate(0 - x, 0 - y);
  if (width > height) pathData.translate(0, (width - height) / 2);
  if (height > width) pathData.translate((height - width) / 2, 0);

  // scale to target size
  pathData.scale(scale, scale);

  return pathData;
};

// extracts element attributes to object
const extractElementProps = (el: Element) => {
  const _props: {[key: string]: string | null} = {};
  for (const name of el.getAttributeNames()) {
    const properName = (() => {
      const parts =  name.split(/[-_:]/i);
      if (parts.length === 1) return parts[0];
      return parts.map((a, i) => {
        if (i > 0) return a.toLowerCase().replace(/^\w/, b => b.toUpperCase());
        return a.toLowerCase();
      }).join('');
    })();
    _props[properName] = el.getAttribute(name);
  }
  return _props;
};

// creates normal svg element icon
export const createSvgIcon = (SvgComponent: React.FunctionComponent) => {
  const element = document.createElement('div');
  element.innerHTML = renderToStaticMarkup(<SvgComponent />);

  const svg = extractSvg(element.children);
  if (!svg) throw new Error('invalid svg: svg not found');

  const boxData = parseBoxData(svg);
  const ns = svg.getAttribute('xmlns');

  svg.removeAttribute('width');
  svg.removeAttribute('height');
  svg.removeAttribute('x');
  svg.removeAttribute('y');
  svg.setAttribute( 'viewBox', `0 0 ${ICON_SIZE} ${ICON_SIZE}`);
  svg.setAttributeNS(ns, 'viewBox', `0 0 ${ICON_SIZE} ${ICON_SIZE}`);

  const fixPaths = (children: HTMLCollection) => {
    for (const child of Array.from(children)) {
      if (!!child.children.length) fixPaths(child.children);

      if (!/^path$/i.test(child.tagName)) continue;

      const pathData = new SVGPathData(child.getAttribute('d') || '');
      fixPathData(pathData, boxData);

      child.setAttribute('d', pathData.encode());
      child.setAttributeNS(ns, 'd', pathData.encode());
    }
  };
  fixPaths(svg.children);

  return (props: SVGProps<SVGSVGElement>) => {
    const svgProps = extractElementProps(svg);
    svgProps.className = 'MuiSvgIcon-root';

    const extraProps = {...props};
    if (extraProps.viewBox) delete extraProps.viewBox;
    if (extraProps.className) {
      svgProps.className = [svgProps.className, extraProps.className].join(' ');
      delete extraProps.className;
    }
    Object.assign(svgProps, extraProps);

    return (
      <svg {...svgProps} dangerouslySetInnerHTML={{__html: svg.innerHTML}} />
    );
  };
};

// creates material-ui based icon
export const createMuiSvgIcon = (SvgComponent: React.FunctionComponent, displayName: string) => {
  const element = document.createElement('div');
  element.innerHTML = renderToStaticMarkup(<SvgComponent />);

  const svg = extractSvg(element.children);
  if (!svg) throw new Error('invalid svg: svg not found');

  // scan recursively to extract all paths of svg
  const paths: string[] = [];
  const scanDom = (children: HTMLCollection) => {
    for (const child of Array.from(children)) {
      if (!!child.children.length) scanDom(child.children);

      if (!/^path$/i.test(child.tagName)) continue;

      const d = child.getAttribute('d');
      if (d) paths.push(d);
    }
  };
  scanDom(svg.children);

  if (!paths.length) throw new Error('invalid svg: no path found');

  const boxData = parseBoxData(svg);
  const pathData = new SVGPathData(paths.join(' '));

  fixPathData(pathData, boxData);
  return MuiUtils.createSvgIcon(React.createElement('path', {d: pathData.encode()}), displayName);
};